import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, User, Settings, BarChart3, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { LogoDark } from "@/components/brand/Logo";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Generate", path: "/generate" },
  { name: "Assessments", path: "/assessments" },
  { name: "CapScan", path: "/capscan" },
  { name: "Coaching", path: "/coaching" },
  { name: "How It Works", path: "/how-it-works" },
  { name: "Pricing", path: "/pricing" },
  { name: "Community", path: "/community" },
  { name: "She Leads", path: "/she-leads" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

function AccountDropdown({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const initials = user.name ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "U";
  const avatarUrl = user.picture || user.avatar_url;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 group" data-testid="account-dropdown-btn">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-gold" />
        ) : (
          <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center border-2 border-gold">
            <span className="font-dm text-gold text-xs font-bold">{initials}</span>
          </div>
        )}
        <span className="font-dm text-white text-sm hidden xl:inline">{user.name?.split(" ")[0]}</span>
        <ChevronDown className={`w-3 h-3 text-white/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
            className="absolute right-0 top-12 bg-white rounded-xl shadow-xl border border-navy/10 w-56 py-2 z-50">
            <div className="px-4 py-3 border-b border-navy/5">
              <p className="font-dm text-navy text-sm font-semibold truncate">{user.name}</p>
              <p className="font-dm text-navy/50 text-xs truncate">{user.email}</p>
              <span className="inline-block bg-gold/10 text-gold font-dm text-[10px] px-2 py-0.5 rounded-full capitalize mt-1">{user.plan}</span>
            </div>
            <Link to="/dashboard" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 font-dm text-sm text-navy/70 hover:bg-cream transition-colors">
              <User className="w-4 h-4" /> Dashboard
            </Link>
            <Link to="/settings" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 font-dm text-sm text-navy/70 hover:bg-cream transition-colors">
              <Settings className="w-4 h-4" /> Account Settings
            </Link>
            {user.role === "admin" && (
              <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 font-dm text-sm text-red-500 hover:bg-red-50 transition-colors">
                <BarChart3 className="w-4 h-4" /> Admin Panel
              </Link>
            )}
            <div className="border-t border-navy/5 mt-1">
              <button onClick={() => { setOpen(false); onLogout(); }} className="flex items-center gap-3 px-4 py-2.5 font-dm text-sm text-navy/50 hover:text-red-500 hover:bg-red-50 transition-colors w-full text-left" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  // Always show bg-navy on pages with their own content
  const alwaysSolid = ["/dashboard", "/settings", "/admin", "/login", "/signup", "/generate"].includes(location.pathname);

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || alwaysSolid ? "bg-navy shadow-lg" : "bg-navy/80 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="nav-logo" style={{ textDecoration: "none" }}>
              <span className="hidden sm:block"><LogoDark width={200} height={46} /></span>
              <span className="sm:hidden"><LogoDark width={160} height={37} /></span>
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="relative text-white font-dm text-[13px] hover:text-gold transition-colors group py-1"
                >
                  {link.name}
                  <span
                    className={`absolute bottom-0 left-0 h-[2px] bg-gold transition-all duration-300 ${
                      location.pathname === link.path ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              ))}
            </div>

            {/* Right Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <a href="https://wa.me/919136262913?text=Hi%20RoleIQ%20Team%2C%20I%20would%20like%20to%20know%20more%20about%20your%20platform." target="_blank" rel="noopener noreferrer"
                className="text-green-400 hover:text-green-300 transition-colors" title="WhatsApp Support">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              {user ? (
                <div className="flex items-center gap-2">
                  <span className="bg-gold/20 text-gold font-dm text-xs font-bold px-2.5 py-1 rounded-full" data-testid="token-balance" title="Competency Map Tokens">
                    M:{user.tokens ?? 0}
                  </span>
                  <span className="bg-navy text-gold border border-gold/40 font-dm text-xs font-bold px-2.5 py-1 rounded-full" data-testid="assessment-token-balance" title="Assessment Tokens">
                    A:{user.assessment_tokens ?? 0}
                  </span>
                  <AccountDropdown user={user} onLogout={handleLogout} />
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="border border-gold text-gold px-5 py-2 rounded-lg font-dm text-sm hover:bg-gold/10 transition-colors"
                    data-testid="nav-login"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-gold text-navy px-5 py-2 rounded-lg font-dm text-sm font-bold hover:bg-gold/90 transition-colors"
                    data-testid="nav-get-started"
                  >
                    Get Started Free
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMobileOpen(true)}
              data-testid="nav-hamburger"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-navy z-[60] flex flex-col items-center justify-center"
          >
            <button
              className="absolute top-5 right-5 text-white p-2"
              onClick={() => setMobileOpen(false)}
            >
              <X className="w-7 h-7" />
            </button>
            <div className="flex flex-col items-center gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-dm text-xl ${
                    location.pathname === link.path ? "text-gold" : "text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-3 mt-4 w-48">
                {user ? (
                  <>
                    <Link to="/dashboard" className="bg-gold text-navy px-5 py-3 rounded-lg font-dm font-bold text-center">Dashboard</Link>
                    <Link to="/settings" className="border border-gold text-gold px-5 py-3 rounded-lg font-dm text-center">Settings</Link>
                    <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="border border-white/20 text-white/70 px-5 py-3 rounded-lg font-dm text-center">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="border border-gold text-gold px-5 py-3 rounded-lg font-dm text-center">Login</Link>
                    <Link to="/signup" className="bg-gold text-navy px-5 py-3 rounded-lg font-dm font-bold text-center">Get Started Free</Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
